import { useState, useEffect } from 'react';
import { EscortProfile } from '@/types';
import { useToast } from '@/hooks/use-toast';
import { getProfileImageUrl } from '@/services/profileImage/profileImageService';
import { useProfileImages } from '@/hooks/useProfileImages';
import { fetchEscortProfileById, fetchEscortServices, fetchEscortRates } from '@/services/profileService';
import { transformProfileData } from '@/utils/profileTransformers';
import { getInitialProfileImageUrl } from '@/services/profileImage';
import { isValidUuid } from '@/services/profileImage/imageUtils';
import { formatStorageUrl } from '@/services/profileImage/imageUtils';

export const useProfileDetail = (profileId: string | undefined) => {
  const [profile, setProfile] = useState<EscortProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  
  const profilesArray = profile ? [profile] : [];
  const { getDisplayImage, isGenerating } = useProfileImages(profilesArray);

  const fetchProfileData = async () => {
    if (!profileId) {
      setError("Profile ID is missing");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch the core profile data
      const profileData = await fetchEscortProfileById(profileId);
      
      // Fetch related data
      const services = await fetchEscortServices(profileId);
      const rates = await fetchEscortRates(profileId);
      
      // Transform the data into our application model
      const transformedProfile = transformProfileData(profileData, services, rates);
      
      console.log("Transformed profile:", transformedProfile);
      setProfile(transformedProfile);
      
      // Removed the success toast message that was previously here
    } catch (error: any) {
      console.error('Error fetching profile:', error);

      let errorMessage = 'Unknown error';
      let errorTitle = "Erreur de chargement";
      
      // Check for potential RLS or permission errors
      if (error?.code === '42501') { // PostgreSQL permission denied
        errorMessage = "Vous n'avez pas la permission de voir ce profil.";
        errorTitle = "Accès refusé";
      } else if (error?.status === 401 || error?.status === 403) { // AuthApiError or similar HTTP errors
         errorMessage = "Accès non autorisé. Veuillez vous connecter ou vérifier vos permissions.";
         errorTitle = "Non autorisé";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      setError(`Error loading profile: ${errorMessage}`);
      
      toast({
        title: errorTitle,
        description: `Impossible de charger le profil: ${errorMessage}`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    console.log("ProfileDetail component mounted with ID:", profileId);
    if (profileId) {
      fetchProfileData();
    } else {
      console.error("No profileId in URL parameters");
      setError("No profile ID provided");
      setIsLoading(false);
    }
  }, [profileId]);

  const isGeneratingImage = profile ? isGenerating.get(profile.id) || false : false;
  const currentProfileImage = profile ? getDisplayImage(profile.id) : '';

  return {
    profile,
    isLoading,
    error,
    profileImage: currentProfileImage,
    isGeneratingImage,
    fetchProfileData
  };
};
